import React, { useState, useEffect } from 'react';
import { Button, Modal, Container, Row, Col, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import {
  useAppContext,
  callNativeMobileApp,
  useTranslator,
} from '@front-activerse/common-components';
import { useCameraCapture } from '../hooks';

interface ScanPlateContainerProps {
  fromUserMeals?: boolean;
}

const ScanPlateContainer: React.FC<ScanPlateContainerProps> = ({ fromUserMeals }) => {
  const navigate = useNavigate();
  const {
    videoRef,
    canvasRef,
    fileInputRef,
    isCameraActive,
    isVideoVisible,
    isSearchingCamera,
    isCapturing: isScanning,
    error,
    isTorchOn,
    isTorchSupported,
    startCamera,
    stopCamera,
    toggleTorch,
    capturePhoto,
    processUploadedFile,
    openGallery,
  } = useCameraCapture();
  const scanPlateDetailsPath = fromUserMeals
    ? '/meals/user-meals/scan-plate/details'
    : '/tracker/scan-plate/details';
  const [showModal, setShowModal] = useState(false);

  const { t } = useTranslator();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      infoWhiteIcon,
      photoGalleryIcon,
      trackerScanFoodTipsImg,
      videoPoster,
    },
  } = useAppContext();

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  // Capture image from camera and navigate to details
  const handleScanPlate = async () => {
    if (isScanning) return;
    try {
      const file = await capturePhoto({ crop: 'square', fileName: 'plate-scan.jpg' });
      await stopCamera();
      navigate(scanPlateDetailsPath, { state: { imageFile: file } });
    } catch (error) {
      console.error('Error capturing image:', error);
    }
  };

  // Handle file upload from disk and navigate to details
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isScanning) return;
    const file = await processUploadedFile(event, { crop: 'square', fileName: 'plate-scan.jpg' });
    if (!file) return;
    await stopCamera();
    navigate(scanPlateDetailsPath, { state: { imageFile: file } });
  };

  // Trigger file input click
  const handleUploadClick = openGallery;

  // Start camera on mount
  useEffect(() => {
    let mounted = true;
    let cleanupCalled = false;

    const initCamera = async () => {
      // Small delay to avoid race conditions with React Strict Mode
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (mounted && !cleanupCalled) {
        console.log('Initializing camera from useEffect');
        await startCamera();
      } else {
        console.log('Component unmounted before camera could start');
      }
    };

    initCamera();

    // Stop camera on navigation back or page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      console.log('handleBeforeUnload called');
      stopCamera();
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      console.log('handlePageHide called');
      stopCamera();
    };

    // Handle browser back/forward navigation (popstate)
    const handlePopState = () => {
      console.log('handlePopState called - user navigated back/forward');
      console.log('isCameraActive', isCameraActive);
      if (isCameraActive) {
        stopCamera();
      } else {
        if (window.location.pathname === '/tracker/scan-plate') {
          startCamera();
        } else {
          stopCamera();
        }
      }
    };

    const handleMessage = (e: MessageEvent) => {
      if (e.data.action === 'release_camera_and_close_modal') {
        handleCloseClick();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('message', handleMessage);

    return () => {
      console.log('useEffect cleanup called - stopping camera');
      mounted = false;
      cleanupCalled = true;
      // Call stopCamera but don't await it (cleanup functions can't be async)
      // The function itself handles proper cleanup
      stopCamera();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseClick = async () => {
    // Stop camera and wait for it to fully stop
    await stopCamera();

    // Give a small delay to ensure camera is fully released
    await new Promise((resolve) => setTimeout(resolve, 100));

    callNativeMobileApp({
      action: 'close_modal',
    });
    navigate(-1);
  };

  return (
    <>
      <div className="scan-plate">
        {/* Camera view - fullscreen */}
        <video
          ref={videoRef}
          className="camera-video-fullscreen"
          playsInline
          muted
          poster={videoPoster}
          style={{ opacity: isVideoVisible ? 1 : 0 }}
        />

        {/* Overlay with controls */}
        <div className="camera-overlay-fullscreen">
          {/* Scan area indicator */}
          {isCameraActive && <div className="scan-area" />}

          {/* Top bar with flash button */}

          <div className="top-controls">
            <button className="scan-plate-button" onClick={handleCloseClick}>
              <img src={closeWhiteIcon} alt="" />
            </button>
            <button className="scan-plate-button" onClick={toggleModal}>
              <img src={infoWhiteIcon} alt="" />
            </button>
          </div>

          {/* Bottom controls */}
          <div className="bottom-controls">
            {error ? (
              <div className="text-center">
                <Alert color="danger" className="mb-0" fade={false}>
                  <p className="mb-0 text-text-main-color text-start">
                    {t('tracker.scanFood.noAccessCamera.notification')}
                  </p>
                </Alert>

                {/* Restart button if not active */}
                {!isCameraActive && !isSearchingCamera && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={() => window.location.reload()}
                    disabled={isSearchingCamera || isScanning}
                  >
                    <p>{t('tracker.scan.button.refreshCamera')}</p>
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Torch button */}
                {isCameraActive && isTorchSupported ? (
                  <button className="scan-plate-button" onClick={toggleTorch} disabled={isScanning}>
                    <img src={isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                  </button>
                ) : (
                  <button className="scan-plate-button" disabled>
                    <img src={flashOffIcon} alt="" />
                  </button>
                )}

                {!isCameraActive && isSearchingCamera && (
                  <p>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {t('tracker.scan.findingCamera')}
                  </p>
                )}

                {isCameraActive && !isSearchingCamera && (
                  <button
                    className="scan-plate-button"
                    onClick={handleScanPlate}
                    disabled={isScanning}
                  >
                    <img src={cameraShutter} alt="" />
                  </button>
                )}

                {/* Upload from gallery button */}
                <button
                  className="scan-plate-button"
                  onClick={handleUploadClick}
                  disabled={isScanning || isSearchingCamera}
                >
                  <img src={photoGalleryIcon} alt="" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Hidden canvas for capturing */}
        <canvas ref={canvasRef} className="d-none" />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileUpload}
        />
      </div>

      <Modal
        className="from-bottom"
        isOpen={showModal}
        fullscreen={true}
        size="lg"
        toggle={toggleModal}
      >
        <div className="select-insert-date">
          <Container>
            <Row>
              <Col xs="12" md={{ size: 4, offset: 4 }}>
                <img className="img-fluid" src={trackerScanFoodTipsImg} alt="" />
                <h2 className="title-medium bold">{t('tracker.tipsModal.title')}</h2>
                <p className="text-text-variant-color mb-xl">
                  {t('tracker.tipsModal.description.photo')}
                </p>

                <Button color="primary" className="w-100" onClick={toggleModal}>
                  {t('tracker.tipsModal.button')}
                </Button>
              </Col>
            </Row>
          </Container>
        </div>
      </Modal>
    </>
  );
};

export default ScanPlateContainer;
